import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

const useStyles = makeStyles(theme => ({
  list: {
    margin: theme.spacing(0.5, 0, 0),
    paddingLeft: theme.spacing(2.5),
  },
  note: {
    color: theme.palette.text.hint,
    marginTop: theme.spacing(2),
  },
}));

const messages = defineMessages({
  title: {
    id: 'actionPlan.regenerateModal.title',
    defaultMessage: 'Regenerate Action Plan',
  },
  description: {
    id: 'actionPlan.regenerateModal.description',
    defaultMessage:
      "Regenerating re-runs Zori's pulse for this member and replaces the AI-suggested draft action items with a new set generated from the latest member data.",
  },
  preservedHeading: {
    id: 'actionPlan.regenerateModal.preservedHeading',
    defaultMessage: 'The following will be preserved:',
  },
  preservedEdited: {
    id: 'actionPlan.regenerateModal.preservedEdited',
    defaultMessage: "Items you've edited or added manually",
  },
  preservedVisible: {
    id: 'actionPlan.regenerateModal.preservedVisible',
    defaultMessage: "AI-suggested items you've made visible to the member",
  },
  note: {
    id: 'actionPlan.regenerateModal.note',
    defaultMessage: 'This may take a few minutes to complete.',
  },
  cancel: {
    id: 'actionPlan.regenerateModal.cancel',
    defaultMessage: 'Cancel',
  },
  regenerate: {
    id: 'actionPlan.regenerateModal.regenerate',
    defaultMessage: 'Regenerate Action Plan',
  },
});

type RegenerateActionPlanModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  disabled?: boolean;
};

export const RegenerateActionPlanModal: React.FC<
  RegenerateActionPlanModalProps
> = ({ isOpen, onDismiss, onConfirm, disabled = false }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const handleRegenerate = () => {
    onConfirm();
    onDismiss();
  };

  return (
    <Modal
      title={formatMessage(messages.title)}
      isOpen={isOpen}
      onDismiss={onDismiss}
      size={1}
      actions={
        <>
          <Button
            onClick={onDismiss}
            variant="outlined"
            color="negative"
            fullWidth
          >
            {formatMessage(messages.cancel)}
          </Button>
          <Button onClick={handleRegenerate} fullWidth disabled={disabled}>
            {formatMessage(messages.regenerate)}
          </Button>
        </>
      }
    >
      <Box direction="column" gap={1.5}>
        <Text>{formatMessage(messages.description)}</Text>
        <Box direction="column" gap={0.5}>
          <Text weight="bold">{formatMessage(messages.preservedHeading)}</Text>
          <ul className={classes.list}>
            <li>
              <Text>{formatMessage(messages.preservedEdited)}</Text>
            </li>
            <li>
              <Text>{formatMessage(messages.preservedVisible)}</Text>
            </li>
          </ul>
        </Box>
        <Text size="caption" className={classes.note}>
          {formatMessage(messages.note)}
        </Text>
      </Box>
    </Modal>
  );
};
